import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useArticleCollections, useBulkAddArticles } from '@/queries/article-collections';
import { CircularLoader } from './CircularLoader';
import CloseIcon from '@/assets/common/close.svg?react';
import CheckIcon from '@/assets/common/check.svg?react';
import FolderIcon from '@/assets/common/folder.svg?react';
import styles from './AddArticleToCollectionsModal.module.scss';

interface AddArticleToCollectionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  articleId: string;
}

export function AddArticleToCollectionsModal({
  isOpen,
  onClose,
  articleId,
}: AddArticleToCollectionsModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: collections, isLoading: loadingCollections } = useArticleCollections();
  const bulkAddArticles = useBulkAddArticles();

  // Filter collections by search term
  const filteredCollections = useMemo(() => {
    if (!collections) return [];

    return collections.filter((collection) => {
      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          collection.name.toLowerCase().includes(term) ||
          (collection.description && collection.description.toLowerCase().includes(term))
        );
      }

      return true;
    });
  }, [collections, searchTerm]);

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) return;

    try {
      // Add article to each selected collection
      await Promise.all(
        Array.from(selectedIds).map((collectionId) =>
          bulkAddArticles.mutateAsync({
            collectionId,
            data: { article_ids: [articleId] },
          })
        )
      );

      setSelectedIds(new Set());
      setSearchTerm('');
      onClose();
    } catch (err) {
      console.error('Failed to add article to collections:', err);
    }
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSearchTerm('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.addArticleToCollectionsModal__overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className={styles.addArticleToCollectionsModal__content}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.addArticleToCollectionsModal__header}>
              <h2 className={styles.addArticleToCollectionsModal__title}>
                Add to Collections
              </h2>
              <button
                className={styles.addArticleToCollectionsModal__closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </button>
            </div>

            <div className={styles.addArticleToCollectionsModal__search}>
              <input
                type="text"
                className={styles.addArticleToCollectionsModal__searchInput}
                placeholder="Search collections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className={styles.addArticleToCollectionsModal__body}>
              {loadingCollections ? (
                <div className={styles.addArticleToCollectionsModal__loading}>
                  <CircularLoader />
                </div>
              ) : filteredCollections.length === 0 ? (
                <div className={styles.addArticleToCollectionsModal__empty}>
                  {searchTerm
                    ? 'No collections match your search'
                    : 'No collections yet. Create one first!'}
                </div>
              ) : (
                <div className={styles.addArticleToCollectionsModal__list}>
                  {filteredCollections.map((collection) => {
                    const isSelected = selectedIds.has(collection.id);
                    return (
                      <div
                        key={collection.id}
                        className={`${styles.addArticleToCollectionsModal__item} ${
                          isSelected ? styles['addArticleToCollectionsModal__item--selected'] : ''
                        }`}
                        onClick={() => handleToggle(collection.id)}
                      >
                        <div
                          className={`${styles.addArticleToCollectionsModal__checkbox} ${
                            isSelected ? styles['addArticleToCollectionsModal__checkbox--checked'] : ''
                          }`}
                        >
                          {isSelected && <CheckIcon />}
                        </div>
                        <div className={styles.addArticleToCollectionsModal__itemIcon}>
                          <FolderIcon />
                        </div>
                        <div className={styles.addArticleToCollectionsModal__itemContent}>
                          <h4 className={styles.addArticleToCollectionsModal__itemTitle}>
                            {collection.name}
                          </h4>
                          <span className={styles.addArticleToCollectionsModal__itemMeta}>
                            {collection.article_count} article{collection.article_count !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={styles.addArticleToCollectionsModal__footer}>
              <span className={styles.addArticleToCollectionsModal__selected}>
                {selectedIds.size} selected
              </span>
              <div className={styles.addArticleToCollectionsModal__actions}>
                <button
                  className={`${styles.addArticleToCollectionsModal__button} ${styles['addArticleToCollectionsModal__button--secondary']}`}
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className={`${styles.addArticleToCollectionsModal__button} ${styles['addArticleToCollectionsModal__button--primary']}`}
                  onClick={handleSubmit}
                  disabled={selectedIds.size === 0 || bulkAddArticles.isPending}
                >
                  {bulkAddArticles.isPending ? 'Adding...' : 'Add to Selected'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
